import React, { useEffect, useMemo, useRef, useState } from "react";

import * as Dialog from "@radix-ui/react-dialog";
import {
  Check,
  History,
  Loader2,
  MessagesSquare,
  Plus,
  RotateCcw,
  SearchX,
  Send,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  X,
} from "lucide-react";

import { SlideChip } from "./BriefingParts";
import { planLabel } from "./flowCopy";
import { withChatFile } from "./planFlow";
import { AskSeed, PlanNav, shortDate } from "./shared";
import {
  AskInput,
  PanelMessage,
  useAccountPlanChat,
} from "../../Hooks/useAccountPlanChat";
import type {
  AccountPlanOverview,
  ChatCitation,
  ChatClaim,
  ChatContext,
  ChatLineReview,
  ChatSource,
  PlanChat,
} from "../../Types/AccountPlan";

export const QUESTION_MAX = 1000;

/** The chat's screen-reader description (F-UF-F30). */
export const CHAT_DESCRIPTION =
  "Ask a question and get an answer from this account's plan, with the slides it came from.";

/** A seed plus a nonce, so opening from the same card twice re-seeds. */
export interface ChatSeed extends AskSeed {
  nonce: number;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  data: AccountPlanOverview;
  nav: PlanNav;
  /** Where the user is on the page; sent with every question. */
  pageContext: ChatContext;
  seed?: ChatSeed | null;
}

const CHIP =
  "rounded-full border border-purple-200 bg-white px-3 py-1.5 text-left text-xs font-medium text-purple-900 transition hover:border-[#974ae4] hover:bg-purple-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:cursor-not-allowed disabled:opacity-50";
const ICON_BTN =
  "rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50";

type Badge = { label: string; className: string };

const SOURCE_BADGE: Partial<Record<NonNullable<ChatSource>, Badge>> = {
  faq: {
    label: "From the briefing Q&A",
    className: "bg-purple-50 text-[#8538d4]",
  },
  stored_answer: {
    label: "From the briefing Q&A",
    className: "bg-purple-50 text-[#8538d4]",
  },
  briefing: {
    label: "From the briefing",
    className: "bg-indigo-50 text-indigo-700",
  },
  slides: {
    label: "From the slides",
    className: "bg-emerald-50 text-emerald-700",
  },
  out_of_scope: {
    label: "Outside what the chat answers",
    className: "bg-gray-100 text-gray-600",
  },
};

/** A line's quotes; chats from before 2026-10-24 carry one {slide, quote}. */
const citationsOf = (c: ChatClaim): ChatCitation[] =>
  c.citations ??
  (c.slide != null && c.quote ? [{ slide: c.slide, quote: c.quote }] : []);

/** One review tag per briefing section the answer used. */
const reviewTags = (claims: ChatClaim[]): ChatLineReview[] => {
  const seen = new Map<string, ChatLineReview>();
  claims.forEach(c => {
    if (c.review && !seen.has(c.review.id)) seen.set(c.review.id, c.review);
  });
  return Array.from(seen.values());
};

const AssistantMessage: React.FC<{
  m: PanelMessage;
  disabled: boolean;
  onOpenSlide: (slide: number) => void;
  onAsk: (question: string) => void;
  onFeedback: (rating: 1 | -1) => void;
}> = ({ m, disabled, onOpenSlide, onAsk, onFeedback }) => {
  const badge = m.source ? SOURCE_BADGE[m.source] : undefined;
  const legacy = m.source === "llm" || m.source === "stored_answer";
  const tags = reviewTags(m.claims);
  const held = m.held_back ?? 0;
  return (
    <div data-testid="chat-answer" className="space-y-2.5">
      {(badge || tags.length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5">
          {badge && (
            <span
              data-testid="chat-source"
              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${badge.className}`}
            >
              <MessagesSquare className="h-3 w-3" aria-hidden /> {badge.label}
            </span>
          )}
          {tags.map(t => (
            <span
              key={t.id}
              data-testid="chat-review"
              className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[11px] text-gray-500"
            >
              {t.title} · {t.verified ? "verified" : "not yet verified"}
            </span>
          ))}
        </div>
      )}
      {m.not_in_plan ? (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
          <SearchX className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div>
            <div className="font-semibold">The plan doesn&apos;t say</div>
            {m.content && !/^The plan doesn.t say\.?$/.test(m.content) && (
              <p className="mt-0.5 whitespace-pre-line">{m.content}</p>
            )}
          </div>
        </div>
      ) : m.source === "out_of_scope" ? (
        <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
          {m.content}
        </p>
      ) : (
        legacy && (
          // Chats from before 2026-10-24: the free-text answer, then its claims.
          <p className="whitespace-pre-line text-sm leading-relaxed text-gray-800">
            {m.content}
          </p>
        )
      )}
      {m.claims.length > 0 && !legacy && (
        // One answer, then the slides it cites (Sushant, 2026-10-26: reads as a message,
        // citations at the end). A line with no text of its own shows its quote.
        <div aria-label="Cited in the plan" className="space-y-2">
          <p className="text-sm leading-relaxed text-gray-800">
            {m.claims.map((c, i) => (
              <span key={i} data-testid="chat-line">
                {i > 0 ? " " : ""}
                {c.text ||
                  citationsOf(c)
                    .map(q => q.quote)
                    .join(" … ")}
              </span>
            ))}
          </p>
          <div
            data-testid="chat-sources"
            className="flex flex-wrap items-center gap-1.5"
          >
            <span className="text-[11px] text-gray-500">Sources:</span>
            {m.claims
              .reduce<number[]>(
                (all, c) => all.concat(citationsOf(c).map(q => q.slide)),
                []
              )
              .filter((n, k, all) => all.indexOf(n) === k)
              .sort((a, b) => a - b)
              .map(n => (
                <SlideChip key={n} slide={n} onOpen={onOpenSlide} />
              ))}
          </div>
        </div>
      )}
      {m.claims.length > 0 && legacy && (
        <ul aria-label="Cited in the plan" className="space-y-2">
          {m.claims.map((c, i) => (
            <li
              key={i}
              data-testid="chat-line"
              className="border-l-2 border-purple-200 pl-2.5"
            >
              <span className="text-xs text-gray-700">
                {c.text ||
                  citationsOf(c)
                    .map(q => q.quote)
                    .join(" … ")}
              </span>
              {citationsOf(c)
                .map(q => q.slide)
                .filter((n, k, all) => all.indexOf(n) === k)
                .map(n => (
                  <span key={n} className="ml-1.5 inline-block align-middle">
                    <SlideChip slide={n} onOpen={onOpenSlide} />
                  </span>
                ))}
            </li>
          ))}
        </ul>
      )}
      {held > 0 && (
        <p data-testid="chat-held-back" className="text-[11px] text-gray-500">
          {held} {held === 1 ? "line" : "lines"} held back: couldn&apos;t be
          found on the slide
        </p>
      )}
      {!!m.nearby?.length && (
        <div className="flex flex-wrap gap-1.5" aria-label="Nearby in the plan">
          {m.nearby.map(n => (
            <button
              key={n.slide}
              type="button"
              className={CHIP}
              onClick={() => onOpenSlide(n.slide)}
            >
              {n.heading} · slide {n.slide}
            </button>
          ))}
        </div>
      )}
      {m.follow_ups.length > 0 && (
        <div
          className="flex flex-wrap gap-1.5"
          aria-label="Follow-up questions"
        >
          {m.follow_ups.map(f => (
            <button
              key={f}
              type="button"
              className={CHIP}
              disabled={disabled}
              onClick={() => onAsk(f)}
            >
              {f}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-1 text-gray-400">
        <button
          type="button"
          aria-label="Helpful"
          aria-pressed={m.feedback === 1}
          onClick={() => onFeedback(1)}
          className={`${ICON_BTN} ${m.feedback === 1 ? "text-[#974ae4]" : ""}`}
        >
          <ThumbsUp className="h-3.5 w-3.5" aria-hidden />
        </button>
        <button
          type="button"
          aria-label="Not helpful"
          aria-pressed={m.feedback === -1}
          onClick={() => onFeedback(-1)}
          className={`${ICON_BTN} ${m.feedback === -1 ? "text-[#974ae4]" : ""}`}
        >
          <ThumbsDown className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
    </div>
  );
};

/**
 * "Ask about this plan" (plan chat, Option A): a right-side sheet over the
 * account-plan page. Every line of an answer carries a quote verified on its
 * slide, from the briefing's stored answers, the briefing, or the slides of
 * the version being viewed (strict chat, 2026-10-24); slide chips open the
 * View source behind the sheet, which stays open.
 */
const ChatPanel: React.FC<Props> = ({
  open,
  onOpenChange,
  accountId,
  data: rawData,
  nav,
  pageContext,
  seed,
}) => {
  // Never an unconfirmed upload (F-UF-F01) or an unpublished draft: the chat
  // service would refuse it.
  // Published versions only, for everyone (2026-11-01).
  const data = useMemo(() => withChatFile(rawData), [rawData]);
  const {
    suggestions,
    chats,
    chat,
    messages,
    pending,
    loading,
    error,
    ask,
    retry,
    newChat,
    openChat,
    feedback,
  } = useAccountPlanChat(accountId, data.current_file_id, open);
  const [draft, setDraft] = useState("");
  const [focus, setFocus] = useState<Omit<
    ChatSeed,
    "prefill" | "nonce"
  > | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  // Opened from a Q&A card or a section: prefill and remember what it's about.
  // Opened plainly ("Ask about this plan"): drop what a card left behind, or the
  // next question would still carry that card's prefill and context.
  useEffect(() => {
    if (!seed) {
      setDraft("");
      setFocus(null);
      return;
    }
    setDraft(seed.prefill);
    setFocus({
      questionId: seed.questionId,
      sectionId: seed.sectionId,
      label: seed.label,
    });
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [seed]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: "end" });
  }, [messages.length, pending]);

  // The chat's file by id: chats store the INTERNAL upload number in
  // `file_version`, so it is never shown or matched against a plan number (D1).
  const fileOf = (id?: string | null) =>
    id ? data.files.find(f => f.id === id) : undefined;
  const answering = chat ? fileOf(chat.file_id) : fileOf(data.current_file_id);
  const version = answering?.plan_version ?? null;
  const draftVersion = chat ? chat.draft : !data.current_review?.published;
  // A version read but not yet written up has no briefing to answer from (PCH-04).
  const answeringStatus = answering?.review_status;
  const hasBriefing = !!answeringStatus && answeringStatus !== "none";

  const send = (input: AskInput) => {
    if (pending) return;
    void ask({
      ...input,
      context: {
        ...pageContext,
        question_id: focus?.questionId ?? pageContext.question_id ?? null,
        section_id: focus?.sectionId ?? pageContext.section_id ?? null,
      },
    });
    setFocus(null);
  };

  const submit = () => {
    const question = draft.trim();
    if (!question) return;
    send({ question });
    setDraft("");
  };

  const pickChat = (c: PlanChat) => {
    setHistoryOpen(false);
    void openChat(c);
  };

  const empty = messages.length === 0 && !loading;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-gray-900/20" />
        <Dialog.Content
          onOpenAutoFocus={e => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
          className="fixed right-0 top-0 z-50 flex h-full w-full max-w-[440px] flex-col border-l border-gray-200 bg-white shadow-xl focus:outline-none"
        >
          <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
            <Sparkles className="h-4 w-4 text-[#974ae4]" aria-hidden />
            <Dialog.Title className="text-sm font-semibold text-gray-900">
              Ask about this plan
            </Dialog.Title>
            {/* F-UF-F30: screen readers hear what the chat answers from. */}
            <Dialog.Description className="sr-only">
              {CHAT_DESCRIPTION}
            </Dialog.Description>
            {version != null && (
              <span
                data-testid="chat-version"
                className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-[10px] text-gray-600"
              >
                v{version} · {draftVersion ? "draft" : "published"}
              </span>
            )}
            <span className="flex-1" />
            <div className="relative">
              <button
                type="button"
                aria-label="Your chats"
                aria-expanded={historyOpen}
                onClick={() => setHistoryOpen(o => !o)}
                className={ICON_BTN}
              >
                <History className="h-4 w-4" aria-hidden />
              </button>
              {historyOpen && (
                <div className="absolute right-0 top-full z-10 mt-1 w-64 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                  {chats.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-gray-500">
                      No earlier chats.
                    </p>
                  ) : (
                    <ul aria-label="Your chats">
                      {chats.map(c => (
                        <li key={c.id}>
                          <button
                            type="button"
                            onClick={() => pickChat(c)}
                            className="flex w-full flex-col px-3 py-1.5 text-left hover:bg-purple-50 focus:bg-purple-50 focus:outline-none"
                          >
                            <span className="truncate text-xs font-medium text-gray-800">
                              {c.title || "Untitled chat"}
                            </span>
                            <span className="text-[11px] text-gray-500">
                              {[
                                planLabel(fileOf(c.file_id), ""),
                                shortDate(c.updated_at),
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              aria-label="New chat"
              onClick={() => {
                newChat();
                setDraft("");
                setFocus(null);
              }}
              className={ICON_BTN}
            >
              <Plus className="h-4 w-4" aria-hidden />
            </button>
            <Dialog.Close aria-label="Close chat" className={ICON_BTN}>
              <X className="h-4 w-4" aria-hidden />
            </Dialog.Close>
          </div>

          {version != null && (
            <div
              data-testid="chat-answering-from"
              className="flex items-center gap-1.5 border-b border-purple-100 bg-purple-50 px-4 py-1.5 text-[11.5px] text-[#8538d4]"
            >
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#974ae4]" />
              {hasBriefing
                ? `Answering from v${version} slides and the ${
                    draftVersion
                      ? "draft briefing as it stands now"
                      : "published briefing"
                  }`
                : `Answering from v${version} slides only (no briefing yet)`}
            </div>
          )}

          <div
            className="flex-1 space-y-4 overflow-y-auto px-4 py-4"
            aria-live="polite"
            aria-busy={pending}
          >
            {loading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2
                  className="h-4 w-4 animate-spin text-[#974ae4]"
                  aria-hidden
                />
                Loading chat…
              </div>
            )}
            {empty && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Ask about this plan. Every line of an answer cites the slide
                  it comes from. When the plan doesn&apos;t say, the chat tells
                  you rather than guess.
                </p>
                {!!suggestions?.standard.length && (
                  <div>
                    <div className="mb-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-400">
                      Briefing questions
                    </div>
                    <div className="flex flex-col items-start gap-1.5">
                      {suggestions.standard.map(q => (
                        <button
                          key={q.id}
                          type="button"
                          className={CHIP}
                          disabled={pending}
                          onClick={() =>
                            send({ question: q.text, questionId: q.id })
                          }
                        >
                          {q.answered && (
                            <Check
                              className="mr-1 inline h-3 w-3 text-emerald-600"
                              aria-label="answered"
                            />
                          )}
                          {q.text}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {!!suggestions?.plan.length && (
                  <div>
                    <div className="mb-1.5 font-mono text-[10px] uppercase tracking-widest text-gray-400">
                      Suggested for this plan
                    </div>
                    <div className="flex flex-col items-start gap-1.5">
                      {suggestions.plan.map(q => (
                        <button
                          key={q}
                          type="button"
                          className={CHIP}
                          disabled={pending}
                          onClick={() => send({ question: q })}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {messages.map(m =>
              m.role === "user" ? (
                <div key={m.id} className="flex flex-col items-end">
                  <div
                    data-testid="chat-question"
                    className="max-w-[85%] whitespace-pre-line rounded-2xl rounded-br-sm bg-purple-50 px-3 py-2 text-sm text-purple-950"
                  >
                    {m.content}
                  </div>
                  {m.failed && (
                    <span className="mt-0.5 text-[11px] text-rose-600">
                      Not answered
                    </span>
                  )}
                </div>
              ) : (
                <AssistantMessage
                  key={m.id}
                  m={m}
                  disabled={pending}
                  onOpenSlide={n => nav.openSlide(n)}
                  onAsk={q => send({ question: q })}
                  onFeedback={r => void feedback(m.id, r)}
                />
              )
            )}

            {pending && (
              <div
                role="status"
                className="flex items-center gap-2 text-sm text-gray-500"
              >
                <Loader2
                  className="h-4 w-4 animate-spin text-[#974ae4]"
                  aria-hidden
                />
                Reading the plan…
              </div>
            )}
            {error && !pending && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-900"
              >
                <span className="flex-1">{error.message}</span>
                {error.retry &&
                  error.kind !== "no_plan" &&
                  error.kind !== "not_confirmed" && (
                    <button
                      type="button"
                      onClick={retry}
                      className="inline-flex shrink-0 items-center gap-1 rounded-md bg-white px-2 py-1 text-xs font-semibold text-rose-800 ring-1 ring-rose-200 hover:bg-rose-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                    >
                      <RotateCcw className="h-3 w-3" aria-hidden /> Retry
                    </button>
                  )}
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form
            className="border-t border-gray-100 px-4 py-3"
            onSubmit={e => {
              e.preventDefault();
              submit();
            }}
          >
            {focus?.label && (
              <div className="mb-2 inline-flex max-w-full items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-[11px] font-medium text-[#8538d4]">
                <span className="truncate" title={focus.label}>
                  About {focus.label}
                </span>
                <button
                  type="button"
                  aria-label="Clear question context"
                  onClick={() => setFocus(null)}
                  className="rounded-full p-0.5 hover:bg-purple-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                >
                  <X className="h-3 w-3" aria-hidden />
                </button>
              </div>
            )}
            <label htmlFor="plan-chat-input" className="sr-only">
              Your question about the plan
            </label>
            <div className="flex items-end gap-2">
              <textarea
                id="plan-chat-input"
                ref={inputRef}
                rows={2}
                value={draft}
                maxLength={QUESTION_MAX}
                disabled={pending}
                placeholder="Ask about priorities, people, risks, cadence…"
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
                className="min-h-[44px] flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#974ae4] focus:outline-none focus:ring-2 focus:ring-[#974ae4]/20 disabled:bg-gray-50"
              />
              <button
                type="submit"
                aria-label="Send"
                disabled={pending || !draft.trim()}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#974ae4] text-white hover:bg-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Send className="h-4 w-4" aria-hidden />
              </button>
            </div>
            {draft.length > QUESTION_MAX - 100 && (
              <div className="mt-1 text-right text-[11px] text-gray-500">
                {draft.length}/{QUESTION_MAX}
              </div>
            )}
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ChatPanel;
